import { ArrowRight, ArrowDownRight } from "lucide-react";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_page-generator-46/artifacts/2ymjsgwo_Xteriorworks1.png";

const STATS = [
  { n: "361×", l: "more bacteria than a toilet seat" },
  { n: "72%", l: "of carts carry fecal bacteria" },
  { n: "$300+", l: "average cart replacement cost" },
];

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative border-b border-[var(--xw-line)]"
    >
      {/* top meta strip */}
      <div className="xw-container pt-6 md:pt-10 pb-4 md:pb-6 flex items-center justify-between">
        <div className="flex items-center gap-2 md:gap-3">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ background: "var(--xw-orange)" }}
          />
          <span className="xw-eyebrow text-[10px] md:text-[11px]">
            Exterior Operations Co.
          </span>
        </div>
        <div className="hidden md:flex items-center gap-6 xw-eyebrow text-[var(--xw-muted)]">
          <span>EST. NORTHEAST OHIO</span>
          <span>FILE 001 / OPERATIONS</span>
        </div>
        <div className="md:hidden xw-eyebrow text-[10px] text-[var(--xw-muted)]">
          FILE 001
        </div>
      </div>

      <div className="xw-container grid grid-cols-12 gap-6 lg:gap-8 pb-12 md:pb-16">
        {/* Image col — appears first on mobile, second on desktop */}
        <div className="col-span-12 lg:col-span-5 lg:order-2 relative">
          <div className="relative h-[300px] sm:h-[440px] lg:h-[640px] overflow-hidden">
            <img
              src={HERO_IMG}
              alt="Xterior Works crew performing exterior operations at a retail storefront at golden hour"
              className="w-full h-full object-cover select-none"
              draggable="false"
            />
            <div className="absolute top-3 left-3 bg-[var(--xw-ink)] text-white px-2.5 py-1 font-mono-xw text-[10px] tracking-[0.18em]">
              FIELD / 02
            </div>
            <div
              className="absolute bottom-0 left-0 right-0 px-3 md:px-4 py-2.5 md:py-3 flex items-center justify-between text-white gap-2"
              style={{ background: "var(--xw-orange)" }}
            >
              <span className="font-mono-xw text-[9px] md:text-[11px] tracking-[0.18em] md:tracking-[0.2em] uppercase">
                Live Route — Ohio Corridor
              </span>
              <span className="font-mono-xw text-[9px] md:text-[11px] tracking-[0.18em] md:tracking-[0.2em] whitespace-nowrap">
                41.4993°N / 81.6944°W
              </span>
            </div>
          </div>
        </div>

        {/* Headline col */}
        <div className="col-span-12 lg:col-span-7 lg:order-1 flex flex-col">
          <h1
            data-testid="hero-headline"
            className="font-display font-black uppercase tracking-[-0.035em] leading-[0.9] md:leading-[0.88]"
            style={{ fontSize: "clamp(2.4rem, 9.5vw, 7rem)" }}
          >
            Your Exterior <br />
            Is Your{" "}
            <span
              className="italic font-medium"
              style={{ fontStyle: "italic", color: "var(--xw-orange)" }}
            >
              First
            </span>{" "}
            <br className="hidden sm:block" />
            <span
              className="italic font-medium"
              style={{ color: "var(--xw-orange)" }}
            >
              Impression.
            </span>
          </h1>

          <div className="mt-6 md:mt-8 max-w-xl">
            <p className="xw-eyebrow text-[var(--xw-orange)] mb-2 md:mb-3 text-[10px] md:text-[11px]">
              Outside Operations. Inside Confidence.
            </p>
            <p className="text-[15px] md:text-[17px] leading-relaxed text-[var(--xw-ink-soft)]">
              Xterior Works manages everything outside your four walls — cart
              sanitation, parking lot maintenance, grounds care, and full
              exterior operations. We serve store owners, facilities managers,
              and operations directors who demand a{" "}
              <strong className="font-semibold">
                documented, professional standard
              </strong>{" "}
              for everything their customers see and touch before they walk
              through your door.
            </p>
          </div>

          <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-3">
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="btn-solid"
            >
              Request a Consultation
              <ArrowRight size={16} />
            </a>
            <a
              href="#why"
              data-testid="hero-cta-secondary"
              className="btn-outline"
            >
              See the Data
              <ArrowDownRight size={16} />
            </a>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="border-t border-[var(--xw-line)] bg-[var(--xw-ink)] text-white">
        <div className="xw-container grid grid-cols-1 md:grid-cols-3">
          {STATS.map((s, i) => (
            <div
              key={s.n}
              data-testid={`hero-stat-${i}`}
              className={`py-7 md:py-10 ${
                i !== 0
                  ? "border-t md:border-t-0 md:border-l border-white/15"
                  : ""
              }`}
            >
              <div className="flex items-baseline gap-3">
                <span
                  className="xw-num text-[44px] sm:text-5xl md:text-6xl"
                  style={{ color: "var(--xw-orange)" }}
                >
                  {s.n}
                </span>
              </div>
              <p className="mt-2 text-white/70 text-sm md:text-base max-w-[28ch]">
                {s.l}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
